import { Injectable, inject } from '@angular/core';
import { Query } from '@datorama/akita';
import { UserAccessRoleType } from '@models/profile/model';
import { CurrentUserState } from '../model';
import { CurrentUserStore } from './current-user.store';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserQuery extends Query<CurrentUserState> {
  protected store: CurrentUserStore;

  public isLoggedIn$ = this.select((state) => !!state.email && !!state.idToken);

  constructor() {
    const store = inject(CurrentUserStore);

    super(store);

    this.store = store;
  }

  public getCognitoPhoneNumber(): string {
    return this.get((state) => state.cognitoPhoneNumber);
  }

  public getEmailAddress(): string {
    return this.get((state) => state.email);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getDecodedToken(): { [id: string]: any } {
    const idToken = this.get((state) => state.idToken);
    if (idToken && idToken.payload) {
      return idToken.payload;
    }

    return null;
  }

  /**
   * Extract the currently scoped division ID from the auth token.
   * @returns DivisionId
   */
  public getDivisionIdFromToken(): string {
    const decodedToken = this.getDecodedToken();
    return decodedToken && decodedToken['custom:division_id'];
  }

  public getUserGroupsFromToken(): UserAccessRoleType[] {
    const decodedToken = this.getDecodedToken();
    if (!decodedToken) {
      return [];
    }
    const userGroupsString = decodedToken['custom:idp_group']?.replace(/[[\] ]/g, '') as string;
    if (userGroupsString.length > 0) {
      return userGroupsString.split(',') as UserAccessRoleType[];
    } else {
      return [];
    }
  }

  public isFullyLoggedIn(): boolean {
    const groups = this.getUserGroupsFromToken() as string[];
    return groups.length > 0 && groups[0] && !(groups.length === 1 && (groups[0] === 'CU' || groups[0] === 'MFA'));
  }

  /**
   * Extract the currently scoped client ID from the auth token. Will be present when
   * isFullyLoggedIn() returns true
   */
  public getClientIdFromToken(): string {
    const decodedToken = this.getDecodedToken();
    return decodedToken && decodedToken['custom:client_id'];
  }

  public getRememberedEmailAddress(): string {
    return this.get((state) => state.rememberedEmail);
  }

  public getVerificationCode(): string {
    return this.get((state) => state.verificationCode);
  }

  private get<T>(project: (state: CurrentUserState) => T): T {
    const state = this.getValue();
    if (state) {
      return project(state);
    }

    return null;
  }
}
